import { logEvent } from '../lib/analytics'
import { useEffect, useState } from 'react'
import { KeepsakeCard } from './KeepsakeCard'
import { Sheet } from './UI'
import { useShareCard } from '../hooks/useShareCard'
import { isOverlayBusy } from '../lib/overlayBusy'
import { capture } from '../lib/batchReceipt'
import { runLine, saveTake } from '../lib/keepsake'
import { supabase } from '../lib/supabase'
import { REEL_LABELS } from './ReelRating'

const EYE = { fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--amber)' }

// ── KeepsakeReveal: the card, handed to you once ──────────────────────────────
// Fires on nextep:keepsake-minted (useWatchlist.setStatus, first completion of a
// show only). Waits its turn behind whatever full-screen moment the same
// completion started — the Final Cut cinematic for an ended series, a canon
// seal — and then rises as a sheet: eyebrow, one serif line, the card, share or
// keep. Only ever for a card that is NEW; re-completing a show mints nothing.
export function KeepsakeReveal() {
  const [k, setK] = useState(null)
  const [ready, setReady] = useState(false)
  const [shareLabel, shareCard, sharing] = useShareCard()
  // "One line for the card?" — the take, typed right onto the new card. Most
  // people never open the episode conversation, so before this the take was
  // usually empty; the reveal is the moment they most want to say something.
  const [take, setTake] = useState('')
  const [takeSaved, setTakeSaved] = useState(false)
  const commitTake = async () => {
    const t = take.trim(); if (!t || !k) return
    const { data } = await supabase.auth.getSession()
    const uid = data?.session?.user?.id
    const saved = await saveTake(uid, k.show_id, t).catch(() => null)
    setK(prev => prev ? { ...prev, take: saved || t } : prev); setTakeSaved(true)
    try { navigator.vibrate?.(8) } catch {}
  }

  useEffect(() => {
    // A cascade's receipt lists the new card with a "See it" that replays this event.
    const on = (e) => { const d = e.detail; if (d?.isNew && d.show_id && !capture('keepsake', e)) { setK(d); setReady(false); setTake(''); setTakeSaved(false) } }
    window.addEventListener('nextep:keepsake-minted', on)
    return () => window.removeEventListener('nextep:keepsake-minted', on)
  }, [])

  useEffect(() => {
    if (!k || ready) return
    const tick = () => { if (!isOverlayBusy() && !window.__nextepModalHold) { setReady(true); logEvent('cinematic_shown', { kind: 'keepsake', show_id: k.show_id }) } }
    tick()
    const iv = setInterval(tick, 300)
    return () => clearInterval(iv)
  }, [k, ready])

  if (!k || !ready) return null
  const rk = k.rating ? Math.round(k.rating * 2) / 2 : 0
  const rl = rk ? REEL_LABELS[rk] : null
  const close = () => setK(null)
  const share = () => shareCard({ show: { poster_path: k.poster_path, name: k.show_name }, eyebrow: `Final Cut · No. ${k.no || ''}`.trim(), headline: k.show_name, line: [rl ? `${rl.label} · ${rk}/5` : null, runLine(k)].filter(Boolean).join(' · '), quote: k.take || null })
  return (
    <Sheet open onClose={close} label="Your card" from="bottom" panelStyle={{ padding: '10px 20px calc(24px + env(safe-area-inset-bottom))', background: 'var(--bg-raised)' }}>
      <div style={{ width: 36, height: 4, borderRadius: 999, background: 'var(--border)', margin: '0 auto 16px' }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={EYE}>Your card · No. {k.no}</div>
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 500, color: 'var(--text-strong)', lineHeight: 1.15, marginTop: 4 }}>That one’s yours to keep.</div>
        </div>
        <button onClick={close} aria-label="Close" className="ui-hit" style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 'var(--radius-full)', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-muted)', fontSize: 18, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
      </div>
      <div style={{ fontSize: 13.5, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>One card for every show you finish. It lives on your profile.</div>
      <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0 4px', '--nxa': 'nxKeepsakeIn 520ms cubic-bezier(0.22,1.2,0.36,1) both' }}>
        <KeepsakeCard k={k} width={Math.min(300, Math.round(window.innerWidth * 0.72))} />
      </div>
      {!k.take && !takeSaved && (
        <div style={{ marginTop: 18 }}>
          <div style={{ ...EYE, marginBottom: 6 }}>One line for the card?</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea value={take} onChange={e => setTake(e.target.value.slice(0, 140))} rows={1} maxLength={140} placeholder="The thing you’d tell a friend"
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitTake() } }}
              style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', borderBottom: '1px solid var(--border)', borderRadius: 0, padding: '6px 0 8px', color: 'var(--text-strong)', fontSize: 15, fontFamily: 'var(--font)', resize: 'none', outline: 'none', lineHeight: 1.4 }} />
            <button onClick={commitTake} disabled={!take.trim()} className="ui-hit" style={{ height: 34, padding: '0 14px', borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)', background: take.trim() ? 'var(--amber)' : 'var(--bg-card)', color: take.trim() ? 'var(--amber-ink)' : 'var(--text-dim)', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 600, cursor: take.trim() ? 'pointer' : 'default', flexShrink: 0 }}>Put it on</button>
          </div>
        </div>
      )}
      {takeSaved && <div style={{ ...EYE, marginTop: 14, color: 'var(--text-dim)' }}>On the card.</div>}
      <button onClick={share} disabled={sharing} aria-busy={sharing || undefined} className="ui-hit cta-glow" style={{ marginTop: 20, width: '100%', height: 46, borderRadius: 'var(--radius-pill)', border: 'none', background: 'var(--amber)', color: 'var(--amber-ink)', fontFamily: 'var(--font)', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
        {shareLabel}
      </button>
      <button onClick={close} style={{ marginTop: 8, width: '100%', height: 42, borderRadius: 'var(--radius-pill)', border: 'none', background: 'none', color: 'var(--text-dim)', fontFamily: 'var(--font)', fontSize: 14, cursor: 'pointer' }}>Done</button>
      <style>{'@keyframes nxKeepsakeIn{from{opacity:0;transform:translateY(18px) rotate(-2deg) scale(0.94)}to{opacity:1;transform:none}}'}</style>
    </Sheet>
  )
}
